const DEFAULT_LIMITS = {
	maxArrayLength: 30,
	maxDepth: 7,
	maxEntries: 80,
	maxStringLength: 1000,
	maxFields: 250,
};

function normalizeKey(key) {
	return String(key || '').toLowerCase().replace(/[^a-z0-9]/g, '');
}

function getMaskKeys(options = {}) {
	return new Set([...(options.maskKeys || [])].map(normalizeKey));
}

function shouldMaskKey(key, maskKeys) {
	const normalized = normalizeKey(key);
	return maskKeys.has(normalized);
}

function maskValue() {
	return '****';
}

function truncateString(value, maxLength) {
	if (value.length <= maxLength) return value;
	return `${value.slice(0, maxLength)}...[TRUNCATED]`;
}

function serializeFormData(formData) {
	const result = {};
	for (const [key, value] of formData.entries()) {
		const serialized = typeof Blob !== 'undefined' && value instanceof Blob ? `[FILE:type=${value.type || 'unknown'},size=${value.size}]` : value;
		if (Object.prototype.hasOwnProperty.call(result, key)) result[key] = Array.isArray(result[key]) ? [...result[key], serialized] : [result[key], serialized];
		else result[key] = serialized;
	}
	return result;
}

function normalizeValue(value) {
	if (value === undefined) return '[undefined]';
	if (value === null || typeof value === 'boolean' || typeof value === 'number') return value;
	if (typeof value === 'bigint') return String(value);
	if (typeof value === 'function' || typeof value === 'symbol') return `[${typeof value}]`;
	if (value instanceof Date) return value.toISOString();
	if (typeof FormData !== 'undefined' && value instanceof FormData) return serializeFormData(value);
	if (typeof Headers !== 'undefined' && value instanceof Headers) return Object.fromEntries(value.entries());
	if (typeof URLSearchParams !== 'undefined' && value instanceof URLSearchParams) return Object.fromEntries(value.entries());
	if (value instanceof Error) {
		return {
			name: value.name,
			message: value.message,
			status: value.status,
			statusCode: value.statusCode,
			data: value.data,
		};
	}
	if (value instanceof Map) return Object.fromEntries(value.entries());
	if (value instanceof Set) return [...value];
	return value;
}

function sanitizeValue(value, state, depth = 0) {
	if (depth > state.limits.maxDepth) return '[TRUNCATED:max-depth]';

	value = normalizeValue(value);
	if (typeof value === 'string') return truncateString(value, state.limits.maxStringLength);
	if (value === null || typeof value !== 'object') return value;
	if (state.seen.has(value)) return '[CIRCULAR]';
	state.seen.add(value);

	if (Array.isArray(value)) {
		const items = value.slice(0, state.limits.maxArrayLength).map(item => sanitizeValue(item, state, depth + 1));
		if (value.length > state.limits.maxArrayLength) items.push(`[TRUNCATED:${value.length - state.limits.maxArrayLength}-items]`);
		return items;
	}

	const result = {};
	const entries = Object.entries(value);
	for (const [index, [key, item]] of entries.entries()) {
		if (index >= state.limits.maxEntries) {
			result.__truncated__ = `${entries.length - state.limits.maxEntries} fields`;
			break;
		}
		result[key] = shouldMaskKey(key, state.maskKeys) ? maskValue() : sanitizeValue(item, state, depth + 1);
	}
	return result;
}

export function sanitizeTrackingPayload(value, options = {}) {
	return sanitizeValue(
		value,
		{
			limits: {...DEFAULT_LIMITS, ...options.limits},
			maskKeys: getMaskKeys(options),
			seen: new WeakSet(),
		},
		0,
	);
}

export function sanitizeTrackingUrl(value, options = {}) {
	if (typeof value !== 'string') return value;
	try {
		const absolute = /^[a-z][a-z\d+.-]*:\/\//i.test(value);
		const url = new URL(value, 'https://hapi.invalid');
		const maskKeys = getMaskKeys(options);
		for (const key of [...url.searchParams.keys()]) {
			if (shouldMaskKey(key, maskKeys)) url.searchParams.set(key, maskValue());
		}
		url.hash = '';
		return truncateString(absolute ? url.toString() : `${url.pathname}${url.search}`, DEFAULT_LIMITS.maxStringLength);
	} catch (_) {
		return truncateString(value, DEFAULT_LIMITS.maxStringLength);
	}
}

export function sanitizeHapiRequest(request = {}, options = {}) {
	const payload = {};
	if (request.url !== undefined) payload.url = sanitizeTrackingUrl(request.url, options);
	if (request.baseURL !== undefined) payload.base_url = sanitizeTrackingUrl(request.baseURL, options);
	if (request.method !== undefined) payload.method = request.method;
	if (request.headers !== undefined) payload.headers = request.headers;
	if (request.body !== undefined) payload.body = request.body;
	return sanitizeTrackingPayload(payload, options);
}

export function flattenTrackingPayload(value, maxFields = DEFAULT_LIMITS.maxFields) {
	const result = {};

	function visit(item, path) {
		if (Object.keys(result).length >= maxFields) return;
		if (Array.isArray(item)) {
			if (!item.length) result[path || 'value'] = '[empty-array]';
			else item.forEach((entry, index) => visit(entry, path ? `${path}.${index}` : String(index)));
			return;
		}
		if (item && typeof item === 'object') {
			const entries = Object.entries(item);
			if (!entries.length) result[path || 'value'] = '[empty-object]';
			else entries.forEach(([key, entry]) => visit(entry, path ? `${path}.${key}` : key));
			return;
		}
		result[path || 'value'] = item;
	}

	visit(value, '');
	if (Object.keys(result).length >= maxFields) result.__truncated__ = `maximum ${maxFields} flattened fields`;
	return result;
}

export function sanitizeSentryTrackingEvent(event, options = {}) {
	if (!event?.contexts?.tracking && !event?.contexts?.hapi_request && !event?.contexts?.hapi_response && !event?.contexts?.hapi_error && !event?.contexts?.error_page) return event;
	const sanitizedEvent = {
		...event,
		contexts: {
			...event.contexts,
		},
	};
	if (event.contexts.tracking) sanitizedEvent.contexts.tracking = sanitizeTrackingPayload(event.contexts.tracking, options);
	if (event.contexts.hapi_request) sanitizedEvent.contexts.hapi_request = sanitizeTrackingPayload(event.contexts.hapi_request, options);
	if (event.contexts.hapi_response) sanitizedEvent.contexts.hapi_response = sanitizeTrackingPayload(event.contexts.hapi_response, options);
	if (event.contexts.hapi_error) sanitizedEvent.contexts.hapi_error = sanitizeTrackingPayload(event.contexts.hapi_error, options);
	if (event.contexts.error_page) sanitizedEvent.contexts.error_page = sanitizeTrackingPayload(event.contexts.error_page, options);
	if (event.request) {
		const request = sanitizeHapiRequest(
			{
				url: event.request.url,
				method: event.request.method,
				headers: event.request.headers,
				body: event.request.data,
			},
			options,
		);
		sanitizedEvent.request = {
			url: request.url,
			method: request.method,
			headers: request.headers,
			data: request.body,
		};
	}
	if (typeof event.message === 'string') sanitizedEvent.message = truncateString(event.message, DEFAULT_LIMITS.maxStringLength);
	if (event.extra) sanitizedEvent.extra = sanitizeTrackingPayload(event.extra, options);
	if (event.tags) sanitizedEvent.tags = sanitizeTrackingPayload(event.tags, {...options, limits: {maxDepth: 2, maxEntries: 30, maxStringLength: 200}});
	if (event.breadcrumbs) {
		sanitizedEvent.breadcrumbs = event.breadcrumbs.map(breadcrumb => ({
			...breadcrumb,
			message: typeof breadcrumb.message === 'string' ? truncateString(breadcrumb.message, DEFAULT_LIMITS.maxStringLength) : breadcrumb.message,
			data: breadcrumb.data ? sanitizeTrackingPayload(breadcrumb.data, {...options, limits: {maxArrayLength: 10, maxDepth: 4, maxEntries: 20, maxStringLength: 500}}) : breadcrumb.data,
		}));
	}
	delete sanitizedEvent.user;
	return sanitizedEvent;
}
